// src/pages/About/AboutPage.jsx
import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Tag,
  Space,
  Divider,
  Form,
  Input,
  Button,
  message,
  Spin,
  Upload,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  GithubOutlined,
  EditOutlined,
  SaveOutlined,
  RollbackOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import BlogLayout from "../../components/BlogLayout";
import { useAuth } from "../../hooks/useBlogAPI";
import { useNavigate } from "react-router-dom";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

// 模拟用户数据存储
const getUserProfile = () => {
  const profile = localStorage.getItem("userProfile");
  return profile ? JSON.parse(profile) : null;
};

const saveUserProfile = (profile) => {
  localStorage.setItem("userProfile", JSON.stringify(profile));

  // 触发自定义事件，通知其他组件用户资料已更新
  window.dispatchEvent(
    new CustomEvent("userProfileUpdated", { detail: profile })
  );
};

const AboutPage = () => {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    // 模拟从服务器获取用户资料
    setTimeout(() => {
      const savedProfile = getUserProfile();
      if (savedProfile) {
        setProfile(savedProfile);
        setAvatarUrl(savedProfile.avatarUrl || null);
        form.setFieldsValue(savedProfile);
      } else {
        // 默认值
        const defaultProfile = {
          name: user?.username || "匿名用户",
          bio: "热爱技术与写作的博主",
          tags: ["前端开发", "React", "JavaScript"],
          direction: "分享前端开发经验和技术心得",
          email: "example@email.com",
          qq: "123456789",
          github: "https://github.com/username",
        };
        setProfile(defaultProfile);
        form.setFieldsValue(defaultProfile);
      }
      setLoading(false);
    }, 500);
  }, [user, form]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (profile) {
      form.setFieldsValue(profile);
      setAvatarUrl(profile.avatarUrl || null);
    }
  };

  const handleCreatePost = () => {
    navigate("/posts/create");
  };

  const onFinish = (values) => {
    try {
      const updatedProfile = {
        ...values,
        avatarUrl: avatarUrl || profile?.avatarUrl || null,
      };
      saveUserProfile(updatedProfile);
      setProfile(updatedProfile);
      setIsEditing(false);
      message.success("资料更新成功！");
    } catch (error) {
      message.error("保存失败，请重试");
    }
  };

  const handleAvatarChange = (info) => {
    if (info.file.status === "done") {
      // 使用本地预览URL
      setAvatarUrl(
        info.file.originFileObj
          ? URL.createObjectURL(info.file.originFileObj)
          : null
      );
    } else if (info.file.status === "error") {
      message.error("头像上传失败");
    }
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("只能上传 JPG/PNG 格式的图片!");
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("图片必须小于 2MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  // 自定义上传请求，使用 FileReader 读取本地文件
  const customRequest = ({ file, onSuccess, onError }) => {
    try {
      // 创建 FileReader 对象读取文件
      const reader = new FileReader();
      reader.onload = () => {
        // 读取完成后，设置头像 URL
        setAvatarUrl(reader.result);
        onSuccess();
      };
      reader.onerror = () => {
        onError();
      };
      reader.readAsDataURL(file); // 读取为 Data URL
    } catch (error) {
      onError(error);
    }
  };

  if (loading) {
    return (
      <BlogLayout user={user} onCreatePost={handleCreatePost}>
        <div className="container">
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        </div>
      </BlogLayout>
    );
  }

  return (
    <BlogLayout user={user} onCreatePost={handleCreatePost}>
      <div className="container">
        <Card
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Title level={2} style={{ margin: 0 }}>
                <UserOutlined /> 关于我
              </Title>
              {!isEditing && user && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  编辑资料
                </Button>
              )}
            </div>
          }
        >
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            {isEditing ? (
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={profile}
              >
                <Form.Item label="头像">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                    }}
                  >
                    <div
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #d9d9d9",
                      }}
                    >
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt="avatar"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <UserOutlined
                          style={{ fontSize: "40px", color: "#ccc" }}
                        />
                      )}
                    </div>
                    <Upload
                      name="avatar"
                      showUploadList={false}
                      beforeUpload={beforeUpload}
                      customRequest={customRequest}
                    >
                      <Button icon={<UploadOutlined />}>更换头像</Button>
                    </Upload>
                  </div>
                </Form.Item>

                <Form.Item
                  label="姓名"
                  name="name"
                  rules={[{ required: true, message: "请输入姓名" }]}
                >
                  <Input placeholder="请输入您的姓名" />
                </Form.Item>

                <Form.Item label="个人简介" name="bio">
                  <Input placeholder="简短的自我介绍" />
                </Form.Item>

                <Form.Item label="个人标签" name="tags" help="按回车添加标签">
                  <SelectTags />
                </Form.Item>

                <Form.Item label="博客内容方向" name="direction">
                  <TextArea placeholder="介绍您的博客主要内容方向" rows={3} />
                </Form.Item>

                <Divider>联系方式</Divider>

                <Form.Item
                  label="邮箱"
                  name="email"
                  rules={[{ type: "email", message: "请输入有效的邮箱地址" }]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="example@email.com"
                  />
                </Form.Item>

                <Form.Item label="QQ" name="qq">
                  <Input placeholder="QQ号码" />
                </Form.Item>

                <Form.Item
                  label="GitHub"
                  name="github"
                  rules={[{ type: "url", message: "请输入有效的网址" }]}
                >
                  <Input
                    prefix={<GithubOutlined />}
                    placeholder="https://github.com/username"
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                    >
                      保存
                    </Button>
                    <Button icon={<RollbackOutlined />} onClick={handleCancel}>
                      取消
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            ) : (
              <div>
                <div style={{ textAlign: "center", marginBottom: 30 }}>
                  <div
                    style={{
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      overflow: "hidden",
                      margin: "0 auto 15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 40,
                      border: "1px solid #d9d9d9",
                    }}
                  >
                    {profile?.avatarUrl ? (
                      <img
                        src={profile.avatarUrl}
                        alt="avatar"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <UserOutlined />
                    )}
                  </div>
                  <Title level={3}>{profile?.name}</Title>
                  <Paragraph type="secondary">{profile?.bio}</Paragraph>
                </div>

                <div style={{ marginBottom: 30 }}>
                  <Title level={4}>个人标签</Title>
                  <div style={{ marginTop: 15 }}>
                    {profile?.tags && profile.tags.length > 0 ? (
                      profile.tags.map((tag, index) => (
                        <Tag
                          color="blue"
                          key={index}
                          style={{
                            fontSize: 14,
                            padding: "5px 10px",
                            margin: "0 8px 8px 0",
                          }}
                        >
                          {tag}
                        </Tag>
                      ))
                    ) : (
                      <Paragraph type="secondary">暂无标签</Paragraph>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: 30 }}>
                  <Title level={4}>博客内容方向</Title>
                  <Paragraph style={{ fontSize: 16, marginTop: 15 }}>
                    {profile?.direction || "暂无介绍"}
                  </Paragraph>
                </div>

                <Divider>联系方式</Divider>

                <div>
                  <Space size="large" wrap>
                    {profile?.email && (
                      <ContactItem
                        icon={<MailOutlined />}
                        label="邮箱"
                        value={profile.email}
                        link={`mailto:${profile.email}`}
                      />
                    )}

                    {profile?.qq && (
                      <ContactItem icon="QQ" label="QQ" value={profile.qq} />
                    )}

                    {profile?.github && (
                      <ContactItem
                        icon={<GithubOutlined />}
                        label="GitHub"
                        value="访问我的GitHub"
                        link={profile.github}
                      />
                    )}
                  </Space>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </BlogLayout>
  );
};

// 自定义标签输入组件
const SelectTags = ({ value = [], onChange }) => {
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue && !value.includes(inputValue)) {
      onChange([...value, inputValue]);
    }
    setInputValue("");
  };

  const handleTagClose = (removedTag) => {
    const newTags = value.filter((tag) => tag !== removedTag);
    onChange(newTags);
  };

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        {value.map((tag, index) => (
          <Tag
            key={index}
            closable
            onClose={() => handleTagClose(tag)}
            style={{ fontSize: 14, padding: "5px 10px", margin: "0 8px 8px 0" }}
          >
            {tag}
          </Tag>
        ))}
      </div>
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onPressEnter={handleInputConfirm}
        placeholder="输入标签后按回车添加"
        style={{ width: 200 }}
      />
    </div>
  );
};

// 联系方式展示组件
const ContactItem = ({ icon, label, value, link }) => {
  const content = link ? (
    <a href={link} target="_blank" rel="noopener noreferrer">
      {value}
    </a>
  ) : (
    <span>{value}</span>
  );

  return (
    <div>
      <div style={{ marginBottom: 5, color: "#666" }}>{label}</div>
      <div
        style={{ fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}
      >
        {typeof icon === "string" ? (
          <span style={{ fontWeight: "bold" }}>{icon}:</span>
        ) : (
          icon
        )}
        {content}
      </div>
    </div>
  );
};

export default AboutPage;
